import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { get, post } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Search, Loader2, FileText, User, CalendarClock, ArrowRight, Lock, LifeBuoy,
  ExternalLink, Send, ShieldQuestion, Info,
} from "lucide-react";

const FALLBACK_EXAMPLES = [
  "What activities are on this week?",
  "How do I volunteer at The Mill?",
  "Where can I find the current safeguarding policy?",
  "How do I book a room?",
  "Where do I get my lunch voucher?",
  "Who do I speak to about expenses?",
];

export function MillGuide({ compact = false }) {
  const [q, setQ] = useState("");
  const [examples, setExamples] = useState(FALLBACK_EXAMPLES);
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    get("/mill/guide/examples").then((d) => d.examples?.length && setExamples(d.examples)).catch(() => {});
  }, []);

  const ask = async (question) => {
    const text = (question ?? q).trim();
    if (!text) return;
    setQ(text);
    setLoading(true);
    try {
      const res = await post("/mill/guide/ask", { question: text });
      setAnswer(res);
    } catch {
      toast.error("The Guide is unavailable right now.");
    }
    setLoading(false);
  };

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-5 shadow-sm sm:p-7" data-testid="mill-guide">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-mill-moss text-white"><Search className="h-4 w-4" /></span>
        <div>
          <h3 className="font-display text-lg font-semibold text-mill-ink">Ask The Mill</h3>
          <p className="text-xs text-mill-muted">Guided information & service navigator</p>
        </div>
      </div>

      <form
        className="mt-4 flex gap-2"
        onSubmit={(e) => { e.preventDefault(); ask(); }}
      >
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. How do I hire the workshop room?"
          className="h-11 rounded-xl"
          data-testid="guide-input"
        />
        <Button type="submit" disabled={loading} className="h-11 rounded-xl bg-mill-moss px-4 hover:bg-mill-moss-dark" data-testid="guide-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>

      <div className="mt-3 flex flex-wrap gap-2" data-testid="guide-chips">
        {examples.slice(0, compact ? 4 : 8).map((ex) => (
          <button
            key={ex}
            onClick={() => ask(ex)}
            data-testid={`guide-chip-${ex.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="rounded-full border border-stone-200 bg-mill-alt px-3 py-1 text-xs text-mill-muted transition-colors hover:border-mill-moss hover:text-mill-ink"
          >
            {ex}
          </button>
        ))}
      </div>

      <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-mill-muted" data-testid="guide-disclaimer">
        <ShieldQuestion className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        The Guide surfaces approved, current Mill information only. It doesn't invent answers, make decisions about
        people, or access private records. All content is synthetic and illustrative.
      </p>

      {answer && <GuideAnswer answer={answer} onAsk={ask} />}
    </div>
  );
}

function GuideAnswer({ answer, onAsk }) {
  const { source, pathways, needs_auth, needs_human } = answer;
  return (
    <div className="mt-5 animate-fade-up rounded-2xl border border-stone-200 bg-mill-alt/40 p-5" data-testid="guide-answer">
      {!answer.matched && (
        <div className="mb-3 flex items-center gap-2 rounded-xl bg-[#FEF3C7] px-3 py-2 text-sm text-[#92400E]" data-testid="guide-no-match">
          <LifeBuoy className="h-4 w-4" /> No confident match — handing over to a human.
        </div>
      )}

      <p className="text-[15px] leading-relaxed text-mill-ink" data-testid="guide-answer-text">{answer.answer}</p>

      {source && (
        <div className="mt-4 grid grid-cols-1 gap-2 rounded-xl border border-stone-200 bg-white p-4 text-sm sm:grid-cols-3" data-testid="guide-source">
          <Meta icon={FileText} label="Source" value={source.title} />
          <Meta icon={User} label="Owner" value={source.owner_role} />
          <Meta icon={CalendarClock} label="Last reviewed" value={source.last_reviewed} />
          <Meta label="Version" value={source.version} plain />
          <Meta label="Status" value={source.status} plain />
          <Meta label="Visibility" value={source.visibility} plain />
        </div>
      )}

      {/* Pathways */}
      <p className="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-mill-muted">What you can do next</p>
      <div className="mt-2 space-y-2">
        {pathways?.public && (
          <PathwayRow tone="public" title="Public guidance" detail={pathways.public.detail}>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-mill-ink" data-testid="pathway-public">
              <ExternalLink className="h-3.5 w-3.5" /> {pathways.public.label}
              <span className="text-[10px] font-normal text-mill-muted">(illustrative page)</span>
            </span>
          </PathwayRow>
        )}

        {pathways?.personal && (
          <PathwayRow tone="personal" title="Personal entitlement" detail={pathways.personal.detail}>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-katlas-receipt bg-[#FFFDF5] px-3 py-1.5 text-xs font-semibold text-[#B45309]" data-testid="pathway-personal">
              <Lock className="h-3.5 w-3.5" /> Sign in — future secure route
            </span>
          </PathwayRow>
        )}

        {pathways?.action && (
          <PathwayRow tone="action" title="Action route" detail="">
            <ActionButton action={pathways.action} />
          </PathwayRow>
        )}
      </div>

      {(needs_auth || needs_human) && (
        <p className="mt-4 flex items-start gap-1.5 rounded-xl bg-white px-3 py-2 text-xs text-mill-muted" data-testid="guide-warning">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {needs_auth
            ? "Confirming a personal entitlement or account status requires a future secure sign-in — the Guide can't see your record."
            : "This step is best handled by a person — the Guide will route you to the right team."}
        </p>
      )}

      {answer.related?.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="guide-related">
          <span className="text-xs text-mill-muted">Related:</span>
          {answer.related.map((rid) => (
            <button key={rid} onClick={() => onAsk(rid.replace(/^c_/, "").replace(/_/g, " "))} className="rounded-full border border-stone-200 bg-white px-2.5 py-1 text-xs text-mill-muted hover:text-mill-ink">
              {rid.replace(/^c_/, "").replace(/_/g, " ")}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Meta({ icon: Icon, label, value, plain }) {
  return (
    <div className={plain ? "" : "sm:col-span-1"}>
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-mill-muted">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </span>
      <span className="mt-0.5 block font-medium capitalize text-mill-ink">{value}</span>
    </div>
  );
}

function PathwayRow({ tone, title, detail, children }) {
  const dot = tone === "public" ? "bg-mill-moss" : tone === "personal" ? "bg-katlas-receipt" : "bg-katlas-blue";
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white px-4 py-3">
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-sm font-semibold text-mill-ink">
          <span className={`h-2 w-2 rounded-full ${dot}`} /> {title}
        </p>
        {detail && <p className="mt-0.5 text-xs leading-snug text-mill-muted">{detail}</p>}
      </div>
      {children}
    </div>
  );
}

function ActionButton({ action }) {
  if (action.kind === "route" || action.kind === "form") {
    return (
      <Link to={action.route} data-testid="pathway-action" className="inline-flex items-center gap-1.5 rounded-md bg-katlas-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-katlas-blue-dark">
        {action.label} <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    );
  }
  if (action.kind === "auth") {
    return <span className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-katlas-receipt bg-[#FFFDF5] px-3 py-1.5 text-xs font-semibold text-[#B45309]" data-testid="pathway-action"><Lock className="h-3.5 w-3.5" /> {action.label}</span>;
  }
  // contact
  return <span className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-mill-ink" data-testid="pathway-action"><User className="h-3.5 w-3.5" /> {action.label}</span>;
}
